import { IconButton, makeStyles } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { CustomNotificationsSidebarItem } from '../../components/Root/CustomNotificationsSidebarItem';
import { CustomSidebarItem } from '../../components/Root/CustomSidebarItem';
import { DraggableMenuSection } from '../../components/Root/DraggableMenuSection';
import { FlyoutProvider } from '../../components/Root/FlyoutContext';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarSpace,
  SidebarPinStateProvider,
  useSidebarPinState,
} from '@backstage/core-components';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SettingsIcon from '@material-ui/icons/Settings';
import SearchIcon from '@material-ui/icons/Search';
import MenuIcon from '@material-ui/icons/Menu';
import { TOP_NAV_HEIGHT } from '../../components/TopNav';
import { MOBILE_MEDIA_QUERY } from '../../components/Root/mobileBreakpoint';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';

const useSidebarStyles = makeStyles(theme => ({
  dividerNoMargin: {
    margin: 0,
  },
  expandButton: {
    width: '100%',
    height: 48,
    borderRadius: 0,
    color: theme.palette.navigation.color,
  },
  '@global': {
    'html, body': {
      margin: 0,
      padding: 0,
    },
    '#root': {
      height: '100%',
    },
  },
}));

const SidebarToggleButton = () => {
  const classes = useSidebarStyles();
  const { isPinned, toggleSidebarPinState } = useSidebarPinState();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });

  if (isMobile) {
    return null;
  }

  const label = isPinned ? 'Collapse Sidebar' : 'Expand Sidebar';
  const Icon = isPinned ? ChevronLeftIcon : ChevronRightIcon;

  return (
    <IconButton
      className={classes.expandButton}
      aria-label={label}
      data-testid="sidebar-expand-button"
      onClick={() => toggleSidebarPinState()}
    >
      <Icon />
    </IconButton>
  );
};

const SidebarNav = () => {
  const classes = useSidebarStyles();
  const parentPinState = useSidebarPinState();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, { noSsr: true });

  return (
    <SidebarPinStateProvider value={{ ...parentPinState, isMobile }}>
      <FlyoutProvider>
        <Sidebar disableExpandOnHover>
          <SidebarGroup label="Navigation" icon={<MenuIcon />}>
            {!isMobile && (
              <div style={{ height: TOP_NAV_HEIGHT, flexShrink: 0 }} />
            )}

            {/* Search - permanent first item, links to /dashboard */}
            <CustomSidebarItem
              icon={SearchIcon}
              to="/dashboard"
              text="Search"
              cannotBeHidden
            />
            <SidebarDivider className={classes.dividerNoMargin} />

            {/* MIDDLE SECTION - Editable: Users can drag-and-drop to reorder and hide items */}
            <DraggableMenuSection />

            <SidebarSpace />
            <SidebarDivider className={classes.dividerNoMargin} />

            {/* BOTTOM SECTION - Non-editable: Notifications and Settings */}
            <CustomNotificationsSidebarItem />
            <CustomSidebarItem
              icon={SettingsIcon}
              to="/settings"
              text="Settings"
              cannotBeHidden
            />

            <SidebarToggleButton />
          </SidebarGroup>
        </Sidebar>
      </FlyoutProvider>
    </SidebarPinStateProvider>
  );
};

export const SidebarContent: ExtensionDefinition = NavContentBlueprint.make({
  params: {
    component: () => <SidebarNav />,
  },
});
